import {
  CommunicationDto,
  DataPackageDto,
  ProjectActivityDto,
  PublicationDto,
  SampleActivityDto,
  SampleDto,
  ShippingDto,
  TheoryActivityDto,
} from '@/api/ApiClient';
import { ProjectUIModel } from '@/lib/models/projectUIModel';
import { useTreeStore } from '@/stores/treeStore';
import type { TreeNode } from '@/types/tree/node';

/**
 * Reactive subscription to whether tree entity data has unsaved local edits.
 * Prefer this over calling `isDataModified()` during render — that store method does not subscribe to `modifiedData`.
 */
export function useIsDataModified(dataId: string | undefined): boolean {
  return useTreeStore((state) => (dataId ? state.modifiedData.has(dataId) : false));
}

/** Subscribe to `state.data` for a single entity id. */
function useTreeEntityData<T>(dataId: string | undefined): T | undefined {
  return useTreeStore((state) => {
    if (!dataId) return undefined;
    return state.data.get(dataId) as T | undefined;
  });
}

function entityDataId(selectedNode: TreeNode | null, types: readonly string[]): string | undefined {
  return selectedNode && types.includes(selectedNode.type) ? selectedNode.dataId : undefined;
}

export function useSampleActivityData(selectedNode: TreeNode | null): SampleActivityDto | undefined {
  return useTreeEntityData<SampleActivityDto>(
    entityDataId(selectedNode, [
      'sampleActivity',
      'sampleActivityInPackage',
      'sampleActivitySnapshot',
      'sampleActivityAggregate',
    ])
  );
}

export function useProjectActivityData(selectedNode: TreeNode | null): ProjectActivityDto | undefined {
  return useTreeEntityData<ProjectActivityDto>(entityDataId(selectedNode, ['projectActivity']));
}

export function useCommunicationData(selectedNode: TreeNode | null): CommunicationDto | undefined {
  return useTreeEntityData<CommunicationDto>(entityDataId(selectedNode, ['communication']));
}

export function useTheoryActivityData(selectedNode: TreeNode | null): TheoryActivityDto | undefined {
  return useTreeEntityData<TheoryActivityDto>(
    entityDataId(selectedNode, ['theoryActivity', 'theoryActivityInPackage'])
  );
}

export function useShippingData(selectedNode: TreeNode | null): ShippingDto | undefined {
  return useTreeEntityData<ShippingDto>(entityDataId(selectedNode, ['shipping']));
}

export function usePublicationData(selectedNode: TreeNode | null): PublicationDto | undefined {
  return useTreeEntityData<PublicationDto>(entityDataId(selectedNode, ['publication', 'publicationInData']));
}

export function useDataPackageData(selectedNode: TreeNode | null): DataPackageDto | undefined {
  return useTreeEntityData<DataPackageDto>(entityDataId(selectedNode, ['dataPackage', 'simulationDataset']));
}

export function useProjectData(selectedNode: TreeNode | null): ProjectUIModel | undefined {
  return useTreeEntityData<ProjectUIModel>(entityDataId(selectedNode, ['project', 'projectInData']));
}

export function useSampleData(selectedNode: TreeNode | null): SampleDto | undefined {
  return useTreeEntityData<SampleDto>(
    entityDataId(selectedNode, ['sample', 'sampleInPackage', 'sampleSnapshot', 'sampleAggregate'])
  );
}

/** Parent sample of an activity node (reactive subscription to tree store). */
export function useParentSampleData(activityNode: TreeNode | null | undefined): SampleDto | undefined {
  const parentNodeId = activityNode?.parentId;
  const parentNode = useTreeStore((state) => (parentNodeId ? state.nodes.get(parentNodeId) : undefined));
  const parentDataId = parentNode?.dataId;
  return useTreeStore((state) => {
    if (!parentDataId) return undefined;
    return state.data.get(parentDataId) as SampleDto | undefined;
  });
}
